import { fetchAuthSession, getCurrentUser } from '@aws-amplify/auth';
import { critical as _crit } from './ChatStreamLogger';

// If the stream goes this long without an event (incl. heartbeat comments —
// those don't surface here because the consumer treats comments as noise)
// we warn that the connection may be silently dead.
const STALE_WARN_MS = 30000;

// Public chat SSE URLs embed `publicSessionToken` as a path segment after
// `/session/`. The token is a bearer secret, so redact it before logging
// while keeping enough path info to debug reconnects.
function redactUrlForLog(url) {
  if (typeof url !== 'string' || !url) return url;
  return url.replace(/\/session\/[^/?#]+/g, '/session/***');
}

function getRetryDelayMs(retryNumber) {
  return Math.min(1000 * Math.pow(2, retryNumber - 1), 10000);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function emitDiagnostic(onStreamDiagnostic, info) {
  if (typeof onStreamDiagnostic === 'function') {
    try {
      onStreamDiagnostic({ ...info, timestamp: Date.now() });
    } catch (_) {}
  }
}

async function consumeJsonSse(response, onEvent, onStreamDiagnostic, logLabel) {
  const reader = response.body?.getReader();
  if (!reader) {
    _crit("SSE", "error", `[${logLabel}] no reader on response body`);
    return { dispatchCount: 0, byteCount: 0, closedByServer: false };
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = null;
  let eventId = null;
  let dataLines = [];
  let dispatchCount = 0;
  let byteCount = 0;
  let lastByteAt = Date.now();
  let staleWarned = false;

  // Watchdog: if we're connected but the TCP stream goes silent (no data, no
  // heartbeat comments) past STALE_WARN_MS, something is wrong — the backend
  // heartbeat scheduler is broken, the LB killed the connection, or similar.
  const staleWatchdog = setInterval(() => {
    const silentMs = Date.now() - lastByteAt;
    if (silentMs > STALE_WARN_MS && !staleWarned) {
      staleWarned = true;
      _crit(
        "SSE",
        "warn",
        `[${logLabel}] STALE — no bytes from server for ${silentMs}ms (heartbeats should arrive every 15s). Connection may be silently dead.`
      );
      emitDiagnostic(onStreamDiagnostic, { event: 'stale', silentMs });
    }
  }, 5000);

  const dispatchEvent = () => {
    if (dataLines.length === 0) return;
    const rawData = dataLines.join('\n');
    dataLines = [];
    try {
      const parsed = JSON.parse(rawData);
      dispatchCount++;
      onEvent(eventName, parsed, eventId);
    } catch {
      _crit("SSE", "error", `[${logLabel}] PARSE ERROR event=${eventName} raw=${rawData.slice(0, 200)}`);
      emitDiagnostic(onStreamDiagnostic, { event: 'parse_error', raw: rawData.slice(0, 200) });
    }
    eventName = null;
    eventId = null;
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        buffer += '\n\n';
      } else {
        lastByteAt = Date.now();
        staleWarned = false;
        byteCount += value?.byteLength || 0;
        buffer += decoder.decode(value, { stream: true });
      }
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const normalizedLine = line.endsWith('\r') ? line.slice(0, -1) : line;
        if (normalizedLine.startsWith('event:')) {
          eventName = normalizedLine.slice(6).trim();
        } else if (normalizedLine.startsWith('data:')) {
          dataLines.push(normalizedLine.slice(5));
        } else if (normalizedLine.startsWith('id:')) {
          eventId = normalizedLine.slice(3).trim();
        } else if (normalizedLine === '') {
          dispatchEvent();
        }
      }

      if (done) {
        return { dispatchCount, byteCount, closedByServer: true };
      }
    }
  } finally {
    clearInterval(staleWatchdog);
  }
}

function emitConnectionState(onConnectionStateChange, state) {
  if (typeof onConnectionStateChange === 'function') {
    onConnectionStateChange(state);
  }
}

export async function streamJsonSseWithRetry({
  url,
  abortController,
  onEvent,
  shouldReconnect,
  onConnectionStateChange,
  onStreamDiagnostic,
  getReconnectUrl,
  maxRetries = 5,
  logLabel = 'SSE stream',
  skipAuth = false,
}) {
  let retries = 0;
  let lastEventId = null;
  let activeUrl = url;

  while (retries <= maxRetries) {
    const connectStartedAt = Date.now();
    try {
      const connState = retries === 0 ? 'connecting' : 'reconnecting';
      emitConnectionState(onConnectionStateChange, connState);
      const loggedUrl = redactUrlForLog(activeUrl);
      if (retries === 0) {
        _crit("SSE", "info", `[${logLabel}] connecting → ${loggedUrl}`);
      } else {
        _crit("SSE", "warn", `[${logLabel}] RECONNECTING (retry ${retries}/${maxRetries}) → ${loggedUrl}`);
      }
      const headers = {
        Accept: 'text/event-stream',
      };
      if (!skipAuth) {
        const { tokens } = await fetchAuthSession();
        const token = tokens.idToken.toString();
        headers.Authorization = `Bearer ${token}`;
        try {
          const user = await getCurrentUser();
          if (user?.username) {
            headers['user-id'] = user.username;
          }
        } catch {
          // Keep stream auth functional even if user lookup is unavailable.
        }
      }

      const response = await fetch(activeUrl, {
        headers,
        signal: abortController.signal,
      });
      if (response.status === 204) {
        _crit("SSE", "info", `[${logLabel}] 204 No Content — session already terminal, closing stream`);
        emitConnectionState(onConnectionStateChange, 'idle');
        return { aborted: false, connectionFailed: false, noContent: true };
      }

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      retries = 0;
      emitConnectionState(onConnectionStateChange, 'connected');
      emitDiagnostic(onStreamDiagnostic, { event: 'connect' });
      _crit("SSE", "info", `[${logLabel}] connected (status=${response.status})`);

      const consumeResult = await consumeJsonSse(
        response,
        (eventName, parsed, eventId) => {
          if (eventId) {
            const numericId = Number(eventId);
            if (Number.isFinite(numericId)) lastEventId = numericId;
          }
          onEvent(eventName, parsed, eventId);
        },
        onStreamDiagnostic,
        logLabel
      );
      const willReconnect = shouldReconnect && shouldReconnect();
      const openMs = Date.now() - connectStartedAt;
      _crit(
        "SSE",
        willReconnect ? "warn" : "info",
        `[${logLabel}] stream closed by server after ${openMs}ms — events=${consumeResult.dispatchCount} bytes=${consumeResult.byteCount} lastEventId=${lastEventId} willReconnect=${!!willReconnect}`
      );

      if (willReconnect) {
        retries++;
        if (typeof getReconnectUrl === 'function' && lastEventId) {
          const reconnectUrl = getReconnectUrl(lastEventId);
          if (reconnectUrl) activeUrl = reconnectUrl;
        }
        emitConnectionState(onConnectionStateChange, 'reconnecting');
        emitDiagnostic(onStreamDiagnostic, { event: 'reconnect', retry: retries });
        await sleep(getRetryDelayMs(retries));
        continue;
      }

      emitConnectionState(onConnectionStateChange, 'idle');
      return { aborted: false, connectionFailed: false };
    } catch (e) {
      if (e?.name === 'AbortError' || abortController.signal.aborted) {
        _crit("SSE", "info", `[${logLabel}] aborted by client`);
        emitConnectionState(onConnectionStateChange, 'idle');
        return { aborted: true, connectionFailed: false };
      }

      retries++;
      const openMs = Date.now() - connectStartedAt;
      if (retries > maxRetries) {
        emitConnectionState(onConnectionStateChange, 'disconnected');
        emitDiagnostic(onStreamDiagnostic, { event: 'disconnect', reason: 'max_retries' });
        _crit("SSE", "error", `[${logLabel}] GAVE UP after ${maxRetries} retries (openMs=${openMs}): ${e?.message || e}`);
        return { aborted: false, connectionFailed: true };
      }

      if (typeof getReconnectUrl === 'function' && lastEventId) {
        const reconnectUrl = getReconnectUrl(lastEventId);
        if (reconnectUrl) activeUrl = reconnectUrl;
      }

      emitConnectionState(onConnectionStateChange, 'reconnecting');
      const delay = getRetryDelayMs(retries);
      _crit("SSE", "warn", `[${logLabel}] errored after ${openMs}ms, retrying in ${delay}ms (${retries}/${maxRetries}): ${e?.message || e}`);
      await sleep(delay);
    }
  }

  emitConnectionState(onConnectionStateChange, 'disconnected');
  return { aborted: false, connectionFailed: true };
}
